import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { ProductLogo } from "./ProductLogo";
import { getDemo } from "../data/demos";

const fade = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const TopBar = () => (
  <header className="sticky top-0 z-40 border-b border-hairline bg-canvas/80 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
      <Link
        to="/"
        data-testid="demo-back"
        className="inline-flex items-center gap-2 text-[14px] font-medium text-slatetx transition-colors hover:text-ink"
      >
        <ArrowLeft size={16} />
        MisiDigi
      </Link>
      <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
        Product Demo
      </span>
    </div>
  </header>
);

const RevealCard = ({ activity, accent }) => (
  <motion.div
    key={activity.title}
    {...fade}
    data-testid="demo-activity"
    className="rounded-[20px] border border-hairline bg-surface p-7 card-shadow sm:p-8"
  >
    <div className="flex items-center justify-between">
      <h3 className="text-[20px] font-bold tracking-tight text-ink">
        {activity.title}
      </h3>
      <span
        className="rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-white"
        style={{ backgroundColor: accent }}
      >
        {activity.tag}
      </span>
    </div>
    <ol className="mt-5 space-y-3">
      {activity.steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-slatetx">
          <span
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold text-white"
            style={{ backgroundColor: accent }}
          >
            {i + 1}
          </span>
          {s}
        </li>
      ))}
    </ol>
  </motion.div>
);

const IdeaCard = ({ card, accent }) => (
  <div
    data-testid="demo-idea-card"
    className="rounded-[20px] border border-hairline bg-surface p-7 card-shadow"
  >
    <div className="flex items-center gap-2">
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: accent }}
      />
      <span
        className="font-mono text-[10px] uppercase tracking-[0.16em]"
        style={{ color: accent }}
      >
        {card.kind}
      </span>
    </div>
    <p className="mt-4 text-[16px] leading-relaxed text-ink">{card.body}</p>
    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-platinum">
      {card.source}
    </p>
  </div>
);

const FramedFadeImage = ({ src, alt, narrow = false }) => (
  <div
    data-testid="demo-framed-image"
    className={`relative overflow-hidden rounded-[20px] border border-hairline bg-surface card-shadow ${
      narrow ? "mx-auto max-w-[540px]" : ""
    }`}
  >
    <img src={src} alt={alt} loading="lazy" className="block w-full" />
    <div
      aria-hidden="true"
      className="absolute inset-0 [mask-image:linear-gradient(to_bottom,transparent_60%,black_90%)]"
    >
      <img src={src} alt="" className="block w-full scale-[1.03] blur-[7px]" />
    </div>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface via-surface/60 to-transparent [mask-image:linear-gradient(to_bottom,transparent_62%,black_95%)]"
    />
  </div>
);

const ProofGrid = ({ proofs }) => (
  <div className="mt-10 grid items-start gap-5 sm:grid-cols-2">
    {proofs.map((p) => (
      <figure
        key={p.image}
        data-testid="demo-proof"
        className="overflow-hidden rounded-[20px] border border-hairline bg-surface card-shadow"
      >
        <img
          src={p.image}
          alt={p.title}
          loading="lazy"
          className="block h-auto w-full bg-mist object-contain"
        />
        <figcaption className="p-5">
          <h3 className="text-[15.5px] font-bold tracking-tight text-ink">
            {p.title}
          </h3>
          <p className="mt-1 text-[13px] leading-relaxed text-slatetx">
            {p.caption}
          </p>
        </figcaption>
      </figure>
    ))}
  </div>
);

const FlowSteps = ({ flow, accent }) => (
  <div className="mt-10">
    <span
      data-testid="demo-flow-label"
      className="inline-flex items-center rounded-full border border-hairline bg-mist px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-steel"
    >
      {flow.label}
    </span>
    <h2 className="mt-4 text-[20px] font-bold tracking-tight text-ink">
      {flow.title}
    </h2>
    <ol className="mt-5 space-y-4">
      {flow.steps.map((s, i) => (
        <li
          key={s.title}
          data-testid="demo-flow-step"
          className="flex gap-3.5 rounded-[18px] border border-hairline bg-surface p-5 card-shadow"
        >
          <span
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-bold text-white"
            style={{ backgroundColor: accent }}
          >
            {i + 1}
          </span>
          <div>
            <h3 className="text-[15.5px] font-bold tracking-tight text-ink">
              {s.title}
            </h3>
            <p className="mt-1 text-[14px] leading-relaxed text-slatetx">
              {s.body}
            </p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

const ResultCard = ({ result, accent }) => (
  <div
    data-testid="demo-result"
    className="mt-6 rounded-[20px] border p-7 card-shadow sm:p-8"
    style={{ borderColor: `${accent}55`, backgroundColor: `${accent}0D` }}
  >
    <span
      className="font-mono text-[10px] uppercase tracking-[0.16em]"
      style={{ color: accent }}
    >
      The result
    </span>
    <h3 className="mt-3 text-[19px] font-bold tracking-tight text-ink">
      {result.title}
    </h3>
    <p className="mt-2 text-[15px] leading-relaxed text-slatetx">
      {result.body}
    </p>
  </div>
);

const Teaser = ({ teaser }) => (
  <div className="mt-4">
    <p className="mb-4 text-center font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
      {teaser.copy}
    </p>
    <div className="grid gap-4 sm:grid-cols-2">
      {teaser.cards.map((c) => (
        <div
          key={c.title}
          data-testid="demo-teaser-card"
          className="relative overflow-hidden rounded-[18px] border border-hairline bg-surface p-6"
        >
          <div className="select-none blur-[3px] [filter:blur(3px)]">
            <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
              {c.tag}
            </span>
            <h4 className="mt-2 text-[17px] font-bold tracking-tight text-ink">
              {c.title}
            </h4>
            <div className="mt-4 space-y-2">
              <span className="block h-2.5 w-full rounded-full bg-mist" />
              <span className="block h-2.5 w-4/5 rounded-full bg-mist" />
              <span className="block h-2.5 w-3/5 rounded-full bg-mist" />
            </div>
          </div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" />
        </div>
      ))}
    </div>
  </div>
);

export const DemoPage = () => {
  const { productId } = useParams();
  const demo = getDemo(productId);
  const [selected, setSelected] = useState(0);

  if (!demo) {
    return (
      <div className="min-h-screen bg-canvas">
        <TopBar />
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <p className="text-[18px] text-slatetx">
            This demo is coming soon.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-[14px] font-semibold text-ink underline-offset-4 hover:underline"
          >
            ← Back to MisiDigi
          </Link>
        </div>
      </div>
    );
  }

  const activity = demo.scenario?.options[selected]?.activity;

  return (
    <div className="min-h-screen bg-canvas">
      <TopBar />
      <div className="mx-auto max-w-4xl px-6 py-10 sm:py-14">
        {/* A. Identity */}
        <motion.div {...fade} className="flex flex-col items-start">
          <ProductLogo
            product={demo}
            rounded="rounded-[22%]"
            className="h-16 w-16 card-shadow"
          />
          <h1 className="mt-4 text-[clamp(2rem,5vw,2.75rem)] font-extrabold tracking-tight text-ink">
            {demo.name}
          </h1>
          <p className="mt-2 max-w-xl text-[17px] leading-relaxed text-slatetx">
            {demo.value}
          </p>
        </motion.div>

        {/* B. Signature experience */}
        {demo.scenario ? (
          <div className="mt-10">
            <p className="text-[18px] font-semibold text-ink">
              {demo.scenario.question}
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {demo.scenario.options.map((o, i) => (
                <button
                  key={o.label}
                  data-testid={`demo-option-${i}`}
                  onClick={() => setSelected(i)}
                  className={`rounded-full border px-4 py-2 text-[14px] font-medium transition-all ${
                    i === selected
                      ? "border-ink bg-ink text-white"
                      : "border-hairline bg-surface text-slatetx hover:border-platinum hover:text-ink"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="mt-7">
              <RevealCard activity={activity} accent={demo.accent} />
            </div>
          </div>
        ) : demo.book ? (
          <div className="mt-10 rounded-[20px] border border-hairline bg-surface p-7 card-shadow sm:p-9">
            <h2 className="text-[24px] font-bold tracking-tight text-ink">
              {demo.book.title}
            </h2>
            <p className="mt-1 text-[14px] text-platinum">{demo.book.author}</p>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.16em] text-platinum">
              What this book is about
            </p>
            <p className="mt-2 text-[15px] leading-relaxed text-slatetx">
              {demo.book.about}
            </p>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.16em] text-platinum">
              Core idea
            </p>
            <p className="mt-2 text-[16px] leading-relaxed text-ink">
              {demo.book.coreIdea}
            </p>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.16em] text-platinum">
              3 practical takeaways
            </p>
            <ol className="mt-3 space-y-3">
              {demo.book.takeaways.map((t, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-slatetx">
                  <span
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-mono text-[10px] font-bold text-white"
                    style={{ backgroundColor: demo.accent }}
                  >
                    {i + 1}
                  </span>
                  {t}
                </li>
              ))}
            </ol>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.16em] text-platinum">
              Why it matters
            </p>
            <p className="mt-2 text-[15px] leading-relaxed text-slatetx">
              {demo.book.why}
            </p>
          </div>
        ) : null}

        {/* Real reader preview — lower-third fade (MindBrief) */}
        {demo.readerImage && (
          <div className="mt-8">
            <FramedFadeImage
              src={demo.readerImage}
              alt={`${demo.name} reader preview`}
              narrow
            />
          </div>
        )}

        {/* Real product proof (screenshots, natural aspect) */}
        {demo.proofs && <ProofGrid proofs={demo.proofs} />}

        {/* Signature experience flow */}
        {demo.flow && <FlowSteps flow={demo.flow} accent={demo.accent} />}

        {/* One meaningful result */}
        {demo.result && <ResultCard result={demo.result} accent={demo.accent} />}

        {/* What makes it different */}
        {demo.highlights && (
          <div
            data-testid="demo-highlights"
            className="mt-10 rounded-[20px] border border-hairline bg-surface p-7 card-shadow sm:p-8"
          >
            <h2 className="text-[20px] font-bold tracking-tight text-ink">
              {demo.highlightsTitle}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {demo.highlights.map((h) => (
                <li
                  key={h}
                  className="flex items-start gap-2.5 text-[14px] leading-relaxed text-slatetx"
                >
                  <span
                    className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: demo.accent }}
                  />
                  {h}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Spotlight — single supporting screenshot */}
        {demo.spotlight && (
          <div className="mt-10">
            <h2 className="text-[20px] font-bold tracking-tight text-ink">
              {demo.spotlight.title}
            </h2>
            <p className="mt-1.5 mb-5 max-w-xl text-[14px] leading-relaxed text-slatetx">
              {demo.spotlight.body}
            </p>
            <div
              data-testid="demo-spotlight"
              className="mx-auto max-w-sm overflow-hidden rounded-[20px] border border-hairline bg-surface card-shadow"
            >
              <img
                src={demo.spotlight.image}
                alt={demo.spotlight.title}
                loading="lazy"
                className="block h-auto w-full object-contain"
              />
            </div>
          </div>
        )}

        {/* Idea / connection cards (MindBrief) */}
        {demo.cards && (
          <div className="mt-4 space-y-4">
            {demo.cards.map((c) => (
              <IdeaCard key={c.kind} card={c} accent={demo.accent} />
            ))}
            {demo.cardsNote && (
              <p
                data-testid="demo-cards-note"
                className="text-[12.5px] italic leading-relaxed text-platinum"
              >
                {demo.cardsNote}
              </p>
            )}
          </div>
        )}

        {/* Visual activity previews (Playvia) */}
        {demo.previews && (
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {demo.previews.map((pv) => (
              <div
                key={pv.title}
                data-testid="demo-preview"
                className="overflow-hidden rounded-[20px] border border-hairline bg-surface card-shadow"
              >
                <div className="aspect-[3/4] overflow-hidden bg-mist">
                  <img
                    src={pv.image}
                    alt={pv.title}
                    loading="lazy"
                    className="h-full w-full object-contain"
                  />
                </div>
                <div className="p-6">
                  <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
                    {pv.label}
                  </span>
                  <h3 className="mt-2 text-[18px] font-bold tracking-tight text-ink">
                    {pv.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slatetx">
                    <span className="font-semibold text-ink">Practices:</span>{" "}
                    {pv.practices}
                  </p>
                  <p className="mt-2 text-[13.5px] italic leading-relaxed text-slatetx">
                    {pv.note}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* How it works — framed guide, lower-third fade (Playvia) */}
        {demo.howTo && (
          <div className="mt-10">
            <h2 className="text-[20px] font-bold tracking-tight text-ink">
              {demo.howTo.title}
            </h2>
            <p className="mt-1.5 mb-5 text-[14px] text-slatetx">
              {demo.howTo.subtitle}
            </p>
            <FramedFadeImage src={demo.howTo.image} alt={demo.howTo.title} />
          </div>
        )}

        {/* C. Value moment */}
        <p className="mt-8 max-w-2xl text-[18px] font-semibold leading-relaxed text-ink">
          {demo.valueMoment}
        </p>

        {/* D. Depth teaser */}
        <div className="mt-10 border-t border-hairline pt-8">
          <Teaser teaser={demo.teaser} />
        </div>

        {/* E. End state */}
        <div className="mt-10 rounded-[20px] border border-hairline bg-mist px-8 py-10 text-center">
          <p className="text-[16px] font-medium text-slatetx">{demo.end}</p>
          <Link
            to="/"
            data-testid="demo-home"
            className="mt-5 inline-block text-[14px] font-semibold text-ink underline-offset-4 hover:underline"
          >
            ← Back to MisiDigi
          </Link>
        </div>
      </div>
    </div>
  );
};
